import { customersRepository } from "@/database/repositories/customersRepository";
import { CustomerWithContacts, InsertCustomer, SelectContact } from "@/database/schema";
import { useEffect, useState } from "react";
import { $archivedCustomers, $customers } from "@/stores";
import { Flip, toast, ToastContainer } from "react-toastify";
import { Contact, TableFilterOption } from "@/types";
import { contactsRepository } from "@/database/repositories/contactsRepository";
import { CustomerForm } from "./customerForm/CustomerForm";
import {
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    TableMeta,
    useReactTable,
    VisibilityState
} from "@tanstack/react-table";
import { customersColumns } from "@/components/customers/CustomersColumns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/table/TableElements";
import { DataTableToolbar } from "@/shared/table/TableToolbar";
import { DataTablePagination } from "@/shared/table/TablePagination";
import { countries } from "@/shared/countriesDropdown/countries";
import { ArchiveRestore, CirclePlus } from "lucide-react";
import { ConfirmDelete } from "@/shared/confirmDelete/ConfirmDelete";
import "./Customers.scss";
import "../../shared/table/Table.scss";
import { useSearchParams } from "react-router-dom";
import { useDb } from "@/database/provider/useDb";

export interface CustomersTableMeta extends TableMeta<CustomerWithContacts> {
    archiveCustomer: (id: number, activeContacts: SelectContact[], archivedContacts: SelectContact[]) => Promise<void>,
    updateCustomer: (customer: InsertCustomer, contacts: Contact[]) => Promise<void>,
    setCustomers: React.Dispatch<React.SetStateAction<CustomerWithContacts[]>>
}

export const Customers = () => {
    const db = useDb();
    const [searchParams] = useSearchParams();
    const [customers, setCustomers] = useState<CustomerWithContacts[]>([]);
    const [deleteManyOpen, setDeleteManyOpen] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const filterCountries: TableFilterOption[] = countries.map(({ name }: { name: string }) => {
        return { value: name, text: name }
    });
    const [rowSelection, setRowSelection] = useState({});
    const [columnSizing, setColumnSizing] = useState({});
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [openOptionsId, setOpenOptionsId] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 50,
    });

    const archiveCustomer = async (id: number, activeContacts: SelectContact[], archivedContacts: SelectContact[]) => {
        try {
            const archived = await customersRepository.archive(db, id);

            const customers = $customers.value.values.filter((item) => item.id !== id);
            setCustomers(customers);
            $customers.set({ isLoad: true, values: customers });
            $archivedCustomers.value.values.unshift({ ...archived, activeContacts, archivedContacts });

            toast.success(`Customer archived!`, {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message)
        }
    }
    const updateCustomer = async (customer: InsertCustomer, contacts: Contact[]) => {
        try {
            const updated = await customersRepository.update(db, customer);
            await Promise.all(
                contacts.map(update => {
                    return contactsRepository.updateContact(db, { ...update, customerId: updated.id })
                })
            );

            const updatedCustomers = await customersRepository.getAllWithContacts(db);
            setCustomers(updatedCustomers);
            $customers.set({ isLoad: true, values: updatedCustomers });

            toast.success("Customer updated!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }

    const addCustomer = async (customer: InsertCustomer, contacts: Contact[]) => {
        try {
            const newCustomer = await customersRepository.add(db, customer);

            const contactsRes = await contactsRepository.addMany(db, contacts.map((contact) => {
                return { ...contact, customerId: newCustomer.id }
            }));

            setCustomers((prevCustomers) => [
                ...prevCustomers,
                { ...newCustomer, activeContacts: contactsRes, archivedContacts: [] }
            ]);

            $customers.set({ isLoad: true, values: customers });
            setIsAddOpen(false);
            toast.success("Customer added!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        }
        catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message)
        }
    }
    const table = useReactTable({
        data: customers,
        columns: customersColumns,
        state: {
            sorting,
            columnVisibility,
            rowSelection,
            columnFilters,
            pagination,
            columnSizing
        },
        meta: {
            type: "Customers",
            openOptionsId,
            setOpenOptionsId,
            archiveCustomer,
            updateCustomer,
            setCustomers
        } as CustomersTableMeta,
        enableRowSelection: true,
        onColumnSizingChange: setColumnSizing,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    const archiveMany = async () => {
        try {
            const ids = table.getSelectedRowModel().rows.map(row => row.original.id);
            await customersRepository.archiveMany(db, ids);

            const customers = await customersRepository.getAllWithContacts(db);

            setCustomers(customers);
            $customers.set({ isLoad: true, values: customers });
            $archivedCustomers.set({
                isLoad: true,
                values: await customersRepository.getAllArchived(db)
            })
            setDeleteManyOpen(false);

            table.toggleAllPageRowsSelected(false);

            toast.success("Customers archived!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
        } catch (err: any) {
            toast.error("Something went wrong!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                theme: "light",
                transition: Flip,
            });
            throw new Error(err.message);
        }
    }

    useEffect(() => {
        try {
            if (!$customers.value.isLoad) {
                customersRepository.getAllWithContacts(db).then((res) => {
                    setCustomers(res);
                    $customers.set({ isLoad: true, values: res });
                })
            }
            else setCustomers($customers.value.values);

            const countriesFilters = searchParams.get("country");

            if (countriesFilters) {
                const statusesArray = countriesFilters.split(",");
                const column = table.getColumn("country");
                column?.setFilterValue(statusesArray);
            }
        }
        catch (err: any) {
            throw new Error(err.message);
        }
    }, [db]);

    return <section className="customers">
        {isAddOpen && <div className="overlay">
            <CustomerForm
                title="Add customer"
                close={() => {
                    setIsAddOpen(false);
                }}
                action={addCustomer}
            />
        </div>}
        {deleteManyOpen && <div className="overlay">
            <ConfirmDelete
                type="Customer"
                action="Archive"
                count={table.getSelectedRowModel().rows.length}
                onClose={() => { setDeleteManyOpen(false) }}
                onDelete={archiveMany} />
        </div>}
        <ToastContainer />
        <div className="customers__container">

            <div className="table-container">
                <div className="table-container__data">
                    <div className="table-container__head">
                        <div className="table__head-container">
                            <h1>Customers</h1>
                            <button className="table__add-button" onClick={() => setIsAddOpen(true)}>
                                <CirclePlus width={18} height={18} />  Add new
                            </button>
                        </div>
                        <div className="table__toolbar-container">
                            <DataTableToolbar table={table}
                                filtersFields={[{ field: "country", title: "Country", options: filterCountries }]}
                                searchField="name" />
                            {table.getSelectedRowModel().rows.length > 0 &&
                                <button className="table__delete-button" onClick={() => {
                                    setDeleteManyOpen(true);
                                }}>
                                    <ArchiveRestore width={14} height={14} /> Archive {table.getSelectedRowModel().rows.length} items
                                </button>
                            }
                        </div>
                    </div>
                    <div className="table-container__table-area">
                        <Table>
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <TableHead key={header.id} style={{ width: `${header.getSize()}%` }}>
                                                    {header.isPlaceholder ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                </TableHead>
                                            )
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows?.length ? (
                                    table.getRowModel().rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            onClick={() => {
                                                if (table.getSelectedRowModel().rows.length > 0) {
                                                    return row.toggleSelected()
                                                }
                                            }}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id} className="table__cell"
                                                    style={{ width: `${cell.column.getSize()}%` }}
                                                    onClick={(e) => {
                                                        const { id } = cell.column;
                                                        if (id === "select" || id === "actions") e.stopPropagation()
                                                    }}>
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (

                                    <div className="empty">
                                        <div className="empty__inner">
                                            <img src="/Eyes.png" alt="No Data" />
                                            <b>No results</b>
                                        </div>
                                    </div>

                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <div className="table-container__pagination">
                    <DataTablePagination table={table} />
                </div>
            </div>

        </div>
    </section>
}

